// WO-47 (MATT, 29/09/2026) — the LinkedIn address of a company.
// Same rules as backend/services/linkedin_links.py: everything after "?" or "#"
// is dropped, and only the company's main page is kept, e.g.
//   linkedin.com/company/2m-kablo-a-s/?originalSubdomain=tr
//     -> https://www.linkedin.com/company/2m-kablo-a-s/
// Mid2Bid stores nothing from LinkedIn: these links only open LinkedIn.

import eventTracker from '../services/eventTracker';

export const LINKEDIN_REFUSAL = {
  en: "Please paste the company's LinkedIn page, not a personal profile.",
  ar: 'يرجى لصق صفحة الشركة على لينكدإن، وليس ملفًا شخصيًا.',
};

const SLUG_RE = /^[^\s/?#<>"']{1,200}$/;

// Returns { kind: 'empty' | 'company' | 'showcase' | 'person' | 'invalid', clean: string | null }
export function parseLinkedin(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return { kind: 'empty', clean: null };
  let text = raw.trim().split(/[?#]/)[0];
  if (!/^https?:\/\//i.test(text)) text = 'https://' + text.replace(/^\/+/, '');
  let url;
  try { url = new URL(text); } catch (e) { return { kind: 'invalid', clean: null }; }
  const host = url.hostname.toLowerCase();
  if (host !== 'linkedin.com' && !host.endsWith('.linkedin.com')) return { kind: 'invalid', clean: null };
  const segs = url.pathname.split('/').filter(Boolean);
  if (!segs.length) return { kind: 'invalid', clean: null };
  const first = segs[0].toLowerCase();
  if (first === 'in' || first === 'pub') return { kind: 'person', clean: null };
  // WO-49: 'school' = a university's or college's LinkedIn page.
  if ((first === 'company' || first === 'showcase' || first === 'school') && segs.length >= 2 && SLUG_RE.test(segs[1])) {
    return { kind: first, clean: `https://www.linkedin.com/${first}/${segs[1]}/` };
  }
  return { kind: 'invalid', clean: null };
}

// What a company card or profile may show. Nothing at all for a personal
// profile, an empty value or anything that is not a LinkedIn page.
export function companyLinkedinLinks(company) {
  const info = parseLinkedin(company?.linkedin_clean || company?.linkedin);
  if (info.kind !== 'company' && info.kind !== 'showcase' && info.kind !== 'school') return null;
  return {
    kind: info.kind,
    page: info.clean,
    people: info.kind === 'company' || info.kind === 'school' ? `${info.clean}people/` : null,
  };
}

// For the company forms: empty, a company page or a showcase page are accepted.
export function isAcceptedCompanyLinkedin(raw) {
  const kind = parseLinkedin(raw).kind;
  return kind === 'empty' || kind === 'company' || kind === 'showcase' || kind === 'school';
}

// Uses the existing analytics endpoint (/api/analytics/events/track).
export function trackLinkedinClick(eventType, company, place) {
  try {
    eventTracker.track(eventType, { company_id: company?._id, ref_id: company?.ref_id, place });
  } catch (e) { /* tracking must never block the link */ }
}
